import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { StatusBadge } from '../components/Shared';
import './Dashboard.css';

export default function DoctorDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || user.role !== 'doctor') {
      navigate('/login?role=doctor');
      return;
    }
    Promise.all([
      axios.get('/api/appointments/doctor'),
      axios.get('/api/doctors/my/profile')
    ]).then(([aRes, pRes]) => {
      setAppointments(aRes.data);
      setProfile(pRes.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [user, navigate]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`/api/appointments/${id}/status`, { status });
      setAppointments(appointments.map(a => a._id === id ? { ...a, status } : a));
    } catch (err) {
      alert(err.response?.data?.message || 'Error updating status');
    }
  };

  const pending = appointments.filter(a => a.status === 'pending');
  const confirmed = appointments.filter(a => a.status === 'confirmed');
  const completed = appointments.filter(a => a.status === 'completed');

  return (
    <div className="dash-page">
      <div className="dash-hero rose-hero">
        <div className="container">
          <div className="dash-hero-inner">
            <div>
              <h1>Welcome, Dr. {user?.firstName} 👨‍⚕️</h1>
              <p>Manage your online consultations</p>
            </div>
            <Link to="/doctor/profile" className="btn btn-white">Edit Profile</Link>
          </div>
        </div>
      </div>

      <div className="container dash-body">
        {!profile?.isVerified && !profile?.verificationPending && (
          <div className="alert alert-warning" style={{ marginBottom: '24px' }}>
            ⏳ Your profile is pending <strong>admin verification</strong>. Once approved, you'll appear in search results and can receive consultations. This typically takes 24-48 hours.
          </div>
        )}
        {profile?.verificationPending && !profile?.isVerified && (
          <div className="alert alert-warning" style={{ marginBottom: '24px' }}>
            🕐 Your profile is <strong>under review</strong>. Admin will verify your credentials within 24–48 hours.
          </div>
        )}

        <div className="dash-stats">
          <div className="stat-card"><div className="s-icon">🔔</div><div className="s-val">{pending.length}</div><div className="s-label">New Requests</div></div>
          <div className="stat-card"><div className="s-icon">🏃</div><div className="s-val">{confirmed.length}</div><div className="s-label">Confirmed</div></div>
          <div className="stat-card"><div className="s-icon">✅</div><div className="s-val">{completed.length}</div><div className="s-label">Completed</div></div>
          <div className="stat-card"><div className="s-icon">⭐</div><div className="s-val">{profile?.rating?.toFixed(1) || 'N/A'}</div><div className="s-label">Rating</div></div>
        </div>

        {/* Doctor Tools */}
        <div className="dash-section">
          <h2>Doctor Tools</h2>
          <div className="quick-actions">
            <Link to="/doctor/profile" className="qa-card rose">
              <span>👤</span><strong>My Profile</strong><p>Update specialization, fee, bio</p>
            </Link>
            <Link to="/doctor/appointments" className="qa-card rose">
              <span>📋</span><strong>All Appointments</strong><p>View and manage consultations</p>
            </Link>
            <Link to="/doctors" className="qa-card rose">
              <span>👀</span><strong>View My Profile</strong><p>See how patients see you</p>
            </Link>
            <a href="https://meet.google.com" target="_blank" rel="noreferrer" className="qa-card rose">
              <span>📹</span>
              <strong>Start Video Call</strong>
              <p>Create Google Meet link</p>
            </a>
          </div>
        </div>

        {/* Pending Requests */}
        {pending.length > 0 && (
          <div className="dash-section">
            <h2>⚡ New Consultation Requests ({pending.length})</h2>
            <div className="bookings-list">
              {pending.map(a => (
                <div key={a._id} className="booking-row">
                  <div className="br-left">
                    <div className="br-avatar rose-av">{a.patient?.firstName?.[0]}</div>
                    <div>
                      <strong>{a.patient?.firstName} {a.patient?.lastName}</strong>
                      <span>📅 {new Date(a.bookingDate).toLocaleDateString()} at {a.timeSlot}</span>
                      <span>📍 {a.patient?.city}</span>
                      {a.notes && <span>📝 {a.notes}</span>}
                    </div>
                  </div>
                  <div className="br-right">
                    <div className="br-amount">₨{a.totalAmount?.toLocaleString()}</div>
                    <button className="btn btn-teal btn-sm" onClick={() => updateStatus(a._id, 'confirmed')}>✅ Accept</button>
                    <button className="btn btn-sm" style={{ background: 'var(--rose-l)', color: 'var(--rose)', borderRadius: '50px', border: 'none', cursor: 'pointer', padding: '8px 16px', fontWeight: 600 }}
                      onClick={() => updateStatus(a._id, 'cancelled')}>❌ Decline</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* All Appointments */}
        <div className="dash-section">
          <div className="section-header">
            <h2>All Appointments</h2>
            <Link to="/doctor/appointments" className="btn btn-outline btn-sm">View All</Link>
          </div>
          {loading ? <div className="page-loader">Loading…</div>
            : appointments.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📋</div>
                <h3>No appointments yet</h3>
                <p>Once your profile is verified, patients can book consultations</p>
              </div>
            ) : (
              <div className="bookings-list">
                {appointments.slice(0, 5).map(a => (
                  <div key={a._id} className="booking-row">
                    <div className="br-left">
                      <div className="br-avatar rose-av">{a.patient?.firstName?.[0]}</div>
                      <div>
                        <strong>{a.patient?.firstName} {a.patient?.lastName}</strong>
                        <span>📅 {new Date(a.bookingDate).toLocaleDateString()} at {a.timeSlot}</span>
                        {a.notes && <span>📝 {a.notes}</span>}
                      </div>
                    </div>
                    <div className="br-right">
                      <StatusBadge status={a.status} />
                      <div className="br-amount">₨{a.totalAmount?.toLocaleString()}</div>
                      {a.status === 'confirmed' && (
                        <button className="btn btn-teal btn-sm" onClick={() => updateStatus(a._id, 'completed')}>Mark Done</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
        </div>
      </div>
    </div>
  );
}
